import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Snackbar, Alert } from '@mui/material'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints, PAYMENT_METHODS } from '../../consts'
import StripeCardForm from './StripeCardForm'
import InlineBankForm from './InlineBankForm'
import logger from '../../utils/logger'
import styles from './PaymentMethodSelector.module.scss'

const METHOD_CONFIG = {
  credit_card: { label: 'Credit Card', desc: 'Visa, Mastercard, Amex', icon: 'credit-card' },
  pad: { label: 'PAD', desc: 'Canadian bank account', icon: 'university' },
  ach: { label: 'ACH Direct Debit', desc: 'US bank account', icon: 'university' },
}

// --- Main Selector ---
const PaymentMethodSelector = ({ customer, onStripePortal, portalLoading, onRefetch }) => {
  const [selectedMethod, setSelectedMethod] = useState(
    customer.preferred_payment_method || 'credit_card',
  )
  const [bankDetails, setBankDetails] = useState(null)
  const [bankLoading, setBankLoading] = useState(true)
  const [snackbar, setSnackbar] = useState(null)

  const availableMethods = Object.entries(PAYMENT_METHODS)
    .filter(([, config]) => {
      if (!customer.country_code) return false
      return config.countries.includes(customer.country_code)
    })
    .map(([key]) => key)

  const methods = availableMethods.length > 0 ? availableMethods : ['credit_card']

  const fetchBankDetails = useCallback(async () => {
    if (!customer.id) return
    setBankLoading(true)
    try {
      const res = await authenticatedFetchJson(apiEndpoints.billingCustomerBankDetails(customer.id))
      setBankDetails(res?.data || null)
    } catch (err) {
      logger.error('Failed to fetch bank details:', err)
      setBankDetails(null)
    } finally {
      setBankLoading(false)
    }
  }, [customer.id])

  // Stripe is the source of truth for billing region. The /stripe-info endpoint
  // detects drift (country_code/currency don't match Stripe's pinned currency)
  // and reconciles it server-side. When that happens, refetch the parent's
  // customer data so the picker re-filters with the corrected country_code.
  const syncRegionFromStripe = useCallback(async () => {
    if (!customer.id || !customer.stripe_customer_id) return
    try {
      const res = await authenticatedFetchJson(apiEndpoints.billingCustomerStripeInfo(customer.id))
      if (res?.data?.synced_country) {
        logger.info('Region auto-synced from Stripe; refetching customer.')
        onRefetch()
      }
    } catch (err) {
      logger.error('Failed to sync region from Stripe:', err)
    }
  }, [customer.id, customer.stripe_customer_id, onRefetch])

  useEffect(() => {
    fetchBankDetails()
  }, [fetchBankDetails])

  useEffect(() => {
    syncRegionFromStripe()
  }, [syncRegionFromStripe])

  useEffect(() => {
    setSelectedMethod(customer.preferred_payment_method || 'credit_card')
  }, [customer.preferred_payment_method])

  // Auto-collapse: if the selected method is no longer valid for the
  // customer's current country, fall back to credit_card. Prevents a stale
  // PAD/ACH form lingering after the country changes underneath it.
  useEffect(() => {
    if (!customer.country_code) return
    const config = PAYMENT_METHODS[selectedMethod]
    if (!config) return
    if (!config.countries.includes(customer.country_code)) {
      setSelectedMethod('credit_card')
    }
  }, [customer.country_code, selectedMethod])

  // Picker tab click is purely a UI change — opens the form for that method
  // without touching preferred_payment_method. Preferred only updates after a
  // form successfully saves a new PM (handleStripeFormSaved / handleBankSaved
  // below). This lets a user open a form, change their mind, and back out
  // without leaving the DB in a stale "preferred" state.
  const handleMethodChange = useCallback(
    (method) => {
      if (method === selectedMethod) return
      setSelectedMethod(method)
    },
    [selectedMethod],
  )

  // After a Stripe form (card or PAD) successfully saves, write preferred to
  // match the freshly-authorized PM type, then refetch. ACH local saves are
  // handled separately — saveBankDetails on the server already sets preferred.
  const handleStripeFormSaved = useCallback(async () => {
    const localType = selectedMethod === 'pad' ? 'pad' : 'credit_card'
    try {
      await authenticatedFetchJson(apiEndpoints.billingCustomerPreferredMethod(customer.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferred_payment_method: localType }),
      })
    } catch (err) {
      logger.error('Failed to update preferred after save:', err)
      setSnackbar({
        severity: 'error',
        message: err.message || 'Saved, but failed to update preferred method.',
      })
    }
    onRefetch()
  }, [customer.id, selectedMethod, onRefetch])

  const handleBankSaved = useCallback(() => {
    fetchBankDetails()
    onRefetch()
  }, [fetchBankDetails, onRefetch])

  return (
    <div className={styles.card} data-testid="payment-method-selector">
      <div className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Update payment method</h2>
      </div>
      <p className={styles.cardSubtitle}>
        Choose how you want to be charged for recurring monthly invoices.
      </p>

      {/* Method cards */}
      <div className={styles.methodGrid}>
        {methods.map((method) => {
          const isSelected = selectedMethod === method
          const cfg = METHOD_CONFIG[method] || {}

          return (
            <button
              key={method}
              type="button"
              className={`${styles.methodCard} ${isSelected ? styles.methodCardActive : ''}`}
              onClick={() => handleMethodChange(method)}
              data-testid={`method-card-${method}`}
            >
              <div className={`${styles.methodIcon} ${isSelected ? styles.methodIconActive : ''}`}>
                <FontAwesomeIcon icon={cfg.icon || 'credit-card'} />
              </div>
              <div>
                <p className={styles.methodLabel}>{cfg.label}</p>
                {method === 'credit_card' ? (
                  <div className={styles.cardBrands}>
                    <FontAwesomeIcon
                      icon={['fab', 'cc-visa']}
                      className={styles.brandIcon}
                      style={{ color: '#1A1F71' }}
                    />
                    <FontAwesomeIcon
                      icon={['fab', 'cc-mastercard']}
                      className={styles.brandIcon}
                      style={{ color: '#EB001B' }}
                    />
                    <FontAwesomeIcon
                      icon={['fab', 'cc-amex']}
                      className={styles.brandIcon}
                      style={{ color: '#2E77BC' }}
                    />
                  </div>
                ) : (
                  <p className={styles.methodDesc}>{cfg.desc}</p>
                )}
              </div>
              {isSelected && (
                <span className={styles.checkBadge}>
                  <FontAwesomeIcon icon="check" />
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Inline form / details area */}
      <div className={styles.formArea}>
        {selectedMethod === 'credit_card' && (
          <div className={styles.creditCardSection}>
            {customer.stripe_customer_id ? (
              <StripeCardForm
                billingCustomerId={customer.id}
                paymentMethodType="card"
                onSaved={handleStripeFormSaved}
                defaultName={
                  customer.billing_contact_name_fallback || customer.account_name || null
                }
                defaultEmail={customer.billing_email_fallback || null}
              />
            ) : (
              <button
                className={styles.saveButton}
                onClick={onStripePortal}
                disabled={portalLoading || !customer.stripe_customer_id}
                type="button"
                data-testid="manage-stripe-btn"
              >
                {portalLoading ? 'Opening…' : 'Manage via Stripe'}
              </button>
            )}
          </div>
        )}

        {selectedMethod === 'pad' && (
          <div className={styles.creditCardSection}>
            {customer.stripe_customer_id ? (
              <StripeCardForm
                billingCustomerId={customer.id}
                paymentMethodType="acss_debit"
                onSaved={handleStripeFormSaved}
                defaultName={
                  customer.billing_contact_name_fallback || customer.account_name || null
                }
                defaultEmail={customer.billing_email_fallback || null}
              />
            ) : (
              <p className={styles.emptyText}>
                Stripe customer not yet linked. Contact your administrator to set up PAD.
              </p>
            )}
          </div>
        )}

        {selectedMethod === 'ach' &&
          (bankLoading && !bankDetails ? (
            <p className={styles.emptyText}>Loading...</p>
          ) : (
            <InlineBankForm
              paymentType="ach"
              billingCustomerId={customer.id}
              onSaved={handleBankSaved}
              existingDetails={bankDetails}
            />
          ))}
      </div>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
            data-testid="payment-method-snackbar"
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </div>
  )
}

PaymentMethodSelector.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.number,
    country_code: PropTypes.string,
    preferred_payment_method: PropTypes.string,
    stripe_customer_id: PropTypes.string,
    payment_method_present: PropTypes.bool,
    default_payment_method_summary_json: PropTypes.object,
  }).isRequired,
  onStripePortal: PropTypes.func.isRequired,
  portalLoading: PropTypes.bool.isRequired,
  onRefetch: PropTypes.func.isRequired,
}

export default PaymentMethodSelector
